/**
 * Der Besucherzähler, Client-Seite.
 *
 * Er beantwortet die eine Frage, die bisher niemand beantworten konnte: schaut
 * sich das überhaupt jemand an? Bis heute sah der Betreiber einen Interessenten
 * erst, wenn dieser bezahlt hat — alle, die die Demo öffnen und wieder gehen,
 * waren unsichtbar.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  DIESE DATEI SPEICHERT NICHTS AUF DEM GERÄT DES BESUCHERS
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Kein Cookie, kein localStorage, keine Kennung. Wer etwas auf dem Endgerät
 * ablegt oder ausliest, braucht die Einwilligung des Besuchers — § 165 Abs. 3
 * TKG 2021, § 25 TDDDG —, und das gälte für eine Zählkennung genauso wie für
 * ein Werbecookie. Ein Zustimmungsbanner vor der Demo würde mehr Interessenten
 * kosten, als dieser Zähler je sichtbar machen kann.
 *
 * Wer der Besucher ist, entscheidet deshalb ausschließlich der Server, aus einem
 * täglich wechselnden Salz. Siehe `supabase/functions/track/index.ts`.
 *
 * Das `sent`-Set unten lebt nur im Arbeitsspeicher dieses Reiters. Es verhindert,
 * dass ein erneutes Rendern dasselbe Ereignis doppelt meldet — es ist keine
 * Wiedererkennung und überlebt kein Neuladen.
 */

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL ?? ''}/functions/v1/track`

export type TrackEvent =
  /** Demo betreten. */
  | 'demo_open'
  /** In der Demo tatsächlich weitergeklickt — der Unterschied zu „kurz reingeschaut". */
  | 'demo_use'
  /** Registrierung begonnen. */
  | 'signup_start'
  /** Registrierung abgeschlossen. */
  | 'signup_done'
  /** Anmeldung eines echten Kontos. */
  | 'login'
  /** Öffentliche Anfrageseite eines Betriebs geöffnet. */
  | 'enquiry_open'

/** Was dieser Reiter schon gemeldet hat. Nur im Arbeitsspeicher. */
const sent = new Set<TrackEvent>()

/**
 * Meldet ein Ereignis. Wirft nie, wartet nie, blockiert nichts.
 *
 * WARUM OHNE `await` UND OHNE FEHLERBEHANDLUNG NACH AUSSEN: dieser Aufruf steht
 * neben dem, was der Besucher eigentlich tun wollte. Ein Zähler, der eine
 * Anmeldung verzögert oder einen Fehler wirft, hat mehr kaputt gemacht, als er
 * je zeigen kann.
 *
 * @param once Nur einmal je Reiter melden. Vorgabe für alles, was beim Öffnen
 *   einer Ansicht ausgelöst wird — sonst zählt jedes erneute Rendern mit.
 */
export function track(event: TrackEvent, once = true): void {
  // In der Entwicklung wird nicht gezählt. Sonst bestünde die Statistik zur
  // Hälfte aus dem Entwickler, der die Seite dreißigmal am Tag neu lädt.
  if (!import.meta.env.PROD) return
  if (once) {
    if (sent.has(event)) return
    sent.add(event)
  }
  if (!ENDPOINT.startsWith('http')) return

  try {
    const body = JSON.stringify({ event })
    // `keepalive`, damit die Meldung auch dann noch hinausgeht, wenn der Besucher
    // im selben Moment weiterklickt oder den Reiter schließt.
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* Gezählt oder nicht — der Besucher erfährt davon nichts. */ })
  } catch { /* dito */ }
}
